import React, { useMemo, useState } from "react";
import emailjs from "@emailjs/browser";
import { addLead } from "utils/leads";

const initialState = {
  fullName: "",
  email: "",
  phone: "",
  company: "",
  message: "",
};

export default function ContactConsultationRequest() {
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const emailJsConfig = useMemo(() => {
    return {
      serviceId: import.meta.env.VITE_EMAILJS_SERVICE_ID,
      templateId: import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
      publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY,
    };
  }, []);

  const canSubmit =
    form.fullName.trim() &&
    form.email.trim() &&
    form.message.trim() &&
    !submitting;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const isEmailJsConfigured = () => {
    return (
      !!emailJsConfig.serviceId &&
      !!emailJsConfig.templateId &&
      !!emailJsConfig.publicKey &&
      !String(emailJsConfig.serviceId).includes("service_xxxxx") &&
      !String(emailJsConfig.templateId).includes("template_xxxxx")
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "idle", message: "" });

    if (!form.fullName.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", message: "Please fill out your name, email, and message." });
      return;
    }

    if (!isEmailJsConfigured()) {
      setStatus({
        type: "error",
        message:
          "Email service is not configured yet. Add your EmailJS values to .env.local then restart the dev server.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const page =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search + window.location.hash
          : "/contact-consultation-request";

      await emailjs.send(
        emailJsConfig.serviceId,
        emailJsConfig.templateId,
        {
          full_name: form.fullName,
          email: form.email,
          phone: form.phone,
          company: form.company,
          message: form.message,
          page,
        },
        { publicKey: emailJsConfig.publicKey }
      );

      // Local-only lead tally (stored in this browser on this device)
      addLead({
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        company: form.company,
        message: form.message,
        page,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      });

      setStatus({ type: "success", message: "Submitted. We will reach out shortly." });
      setForm(initialState);
    } catch (err) {
      setStatus({
        type: "error",
        message: "Something failed while sending. Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: 24 }}>
      <h1 style={{ marginBottom: 8 }}>Request a Consultation</h1>
      <p style={{ marginTop: 0, marginBottom: 20, opacity: 0.85 }}>
        Tell us what you are building and we will respond quickly.
      </p>

      <form onSubmit={onSubmit} style={{ display: "grid", gap: 12 }}>
        <input
          name="fullName"
          placeholder="Full name *"
          value={form.fullName}
          onChange={onChange}
          style={{ padding: 12, borderRadius: 8, border: "1px solid #ddd" }}
        />
        <input
          name="email"
          placeholder="Email *"
          value={form.email}
          onChange={onChange}
          style={{ padding: 12, borderRadius: 8, border: "1px solid #ddd" }}
        />
        <input
          name="phone"
          placeholder="Phone (optional)"
          value={form.phone}
          onChange={onChange}
          style={{ padding: 12, borderRadius: 8, border: "1px solid #ddd" }}
        />
        <input
          name="company"
          placeholder="Company (optional)"
          value={form.company}
          onChange={onChange}
          style={{ padding: 12, borderRadius: 8, border: "1px solid #ddd" }}
        />
        <textarea
          name="message"
          placeholder="How can we help? *"
          value={form.message}
          onChange={onChange}
          rows={6}
          style={{ padding: 12, borderRadius: 8, border: "1px solid #ddd", resize: "vertical" }}
        />

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            padding: 12,
            borderRadius: 10,
            border: "none",
            cursor: canSubmit ? "pointer" : "not-allowed",
          }}
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>

        {status.type !== "idle" && (
          <div
            style={{
              padding: 12,
              borderRadius: 10,
              border: "1px solid #ddd",
              opacity: 0.95,
            }}
          >
            {status.message}
          </div>
        )}
      </form>
    </div>
  );
}
